import i18next from "i18next";
import React, { StrictMode } from "react";
import { I18nextProvider } from "react-i18next";
import ReactDOM from "react-dom/client";

import { App } from "./components/app";
import { init as initI18n } from "./libs/i18n/i18n";
import { API_BASE_URL } from "./common/configs/environment.config";

const ELEMENT_TAG_NAME = "ricoh-chat-client";
const STYLE_FILE_NAME = "ricoh-chat-client.css";
const VERSION =
  typeof __RICOH_CHAT_CLIENT_VERSION__ !== "undefined"
    ? __RICOH_CHAT_CLIENT_VERSION__
    : "1.0.0";
let i18nReadyPromise;

const ensureI18n = () => {
  if (!i18nReadyPromise) {
    i18nReadyPromise = initI18n();
  }

  return i18nReadyPromise;
};

const validateParams = (params = {}) => {
  const { client_id, client_secret, external_user } = params;

  if (!client_id) {
    throw new Error("[RicohChatClient] client_id is required");
  }

  if (!client_secret) {
    throw new Error("[RicohChatClient] client_secret is required");
  }

  if (!external_user) {
    throw new Error("[RicohChatClient] external_user is required");
  }
};

const getCandidateScripts = () => {
  if (typeof document === "undefined") {
    return [];
  }

  const list = [];
  if (document.currentScript) {
    list.push(document.currentScript);
  }

  const scripts = document.getElementsByTagName("script");
  for (let index = scripts.length - 1; index >= 0; index -= 1) {
    const script = scripts[index];
    if (script?.src?.includes("ricoh-chat-client")) {
      list.push(script);
      break;
    }
  }

  return list.filter((script) => Boolean(script?.src));
};

const resolveBaseHref = () => {
  if (typeof window !== "undefined" && window.location?.href) {
    return window.location.href;
  }

  if (typeof globalThis !== "undefined" && globalThis.location?.href) {
    return globalThis.location.href;
  }

  return undefined;
};

const resolveAssetsBaseUrl = () => {
  if (typeof document === "undefined") {
    return "";
  }

  const [candidate] = getCandidateScripts();
  if (!candidate?.src) {
    return "";
  }

  const baseHref = resolveBaseHref();
  if (!baseHref) {
    return "";
  }

  try {
    const scriptUrl = new URL(candidate.src, baseHref);
    const segments = scriptUrl.pathname.split("/");
    segments.pop();
    scriptUrl.pathname = `${segments.join("/")}/`;
    scriptUrl.search = "";
    scriptUrl.hash = "";
    return scriptUrl.toString();
  } catch {
    return "";
  }
};

const assetBaseUrl = resolveAssetsBaseUrl();
const stylesheetHref = (() => {
  if (!assetBaseUrl) {
    return "";
  }

  const base = assetBaseUrl.endsWith("/") ? assetBaseUrl : `${assetBaseUrl}/`;
  const versionSuffix = VERSION ? `?v=${encodeURIComponent(VERSION)}` : "";
  return `${base}${STYLE_FILE_NAME}${versionSuffix}`;
})();

const createStylesheetLink = () => {
  if (typeof document === "undefined" || !stylesheetHref) {
    return null;
  }

  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = stylesheetHref;
  link.setAttribute("data-ricoh-chat-style", "true");
  return link;
};

class RicohChatElement extends HTMLElement {
  constructor() {
    super();
    this._shadowRoot = this.attachShadow({ mode: "open" });
    this._container = document.createElement("div");
    this._container.className = "ricoh-chat-client-root";
    this._overlayHost = document.createElement("div");
    this._overlayHost.className = "ricoh-chat-client-overlays";
    this._overlayHost.setAttribute("data-ricoh-chat-overlay", "true");
    this._overlayHost.setAttribute("aria-hidden", "true");
    this._root = null;

    const stylesheet = createStylesheetLink();
    if (stylesheet) {
      this._shadowRoot.appendChild(stylesheet);
    }

    this._shadowRoot.appendChild(this._container);
    this._shadowRoot.appendChild(this._overlayHost);
  }

  async render(params = {}) {
    const { client_id, client_secret, external_user, onClose, hasMenu, quickQuestions, session_id, onSessionChange } = params;
    validateParams({ client_id, client_secret, external_user });

    if (!this._root) {
      this._root = ReactDOM.createRoot(this._container);
    }

    await ensureI18n();

    this._root.render(
      React.createElement(
        I18nextProvider,
        { i18n: i18next },
        React.createElement(App, {
          client_id,
          client_secret,
          external_user,
          onClose,
          hasMenu,
          quickQuestions,
          session_id,
          onSessionChange,
          styleContainer: this._shadowRoot,
          portalContainer: this._overlayHost,
        })
      )
    );

    return this._root;
  }

  destroy() {
    if (!this._root) {
      return;
    }

    this._root.unmount();
    this._root = null;
  }

  disconnectedCallback() {
    this.destroy();
  }
}

const registerElement = () => {
  if (!globalThis.customElements) {
    console.error("[RicohChatClient] Custom Elements API is not supported in this browser.");
    return;
  }

  if (!globalThis.customElements.get(ELEMENT_TAG_NAME)) {
    globalThis.customElements.define(ELEMENT_TAG_NAME, RicohChatElement);
  }
};

registerElement();

const findHostElement = (target = ELEMENT_TAG_NAME) => {
  if (typeof document === "undefined") {
    return null;
  }

  if (target instanceof RicohChatElement) {
    return target;
  }

  if (target instanceof HTMLElement && target.tagName.toLowerCase() === ELEMENT_TAG_NAME) {
    return target;
  }

  const selector = typeof target === "string" ? target : ELEMENT_TAG_NAME;
  const element = document.querySelector(selector);

  if (element instanceof RicohChatElement) {
    return element;
  }

  return null;
};

const ensureHostElement = (target = ELEMENT_TAG_NAME) => {
  const element = findHostElement(target);

  if (!element) {
    throw new Error(`[RicohChatClient] Unable to find <${ELEMENT_TAG_NAME}> using selector: ${target}`);
  }

  return element;
};

const render = async (params = {}) => {
  const { selector = ELEMENT_TAG_NAME, ...rest } = params;
  const element = ensureHostElement(selector);
  return element.render(rest);
};

const destroy = (selector = ELEMENT_TAG_NAME) => {
  const element = findHostElement(selector);
  if (!element) {
    return false;
  }

  element.destroy();
  return true;
};

/**
 * Verifies that the given credentials are valid by calling POST /auth/agent.
 * Returns true if the credentials are accepted, false otherwise.
 *
 * @param {string} client_id
 * @param {string} client_secret
 * @returns {Promise<boolean>}
 */
const authen = async (client_id, client_secret) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/agent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ agent_id: client_id, agent_secret: client_secret }),
    });
    return response.ok;
  } catch {
    return false;
  }
};

const namespace = globalThis.RicohChatClient || {};

const emitControlEvent = (type, detail) => {
  globalThis.dispatchEvent?.(
    new CustomEvent(`ricoh-chat-client:${type}`, {
      detail,
      bubbles: true,
      composed: true,
    })
  );
};

namespace.render = render;
namespace.destroy = destroy;
namespace.authen = authen;
namespace.version = VERSION;
namespace.apiURL = API_BASE_URL;
namespace.elementTag = ELEMENT_TAG_NAME;
namespace.requestClose = () => {
  emitControlEvent("close-request", { source: "api" });
};

globalThis.RicohChatClient = namespace;